import axios, { post } from 'axios';
import moment from 'moment';
import { ROOT_URL_V2, getCSRFToken, ROOT_URL } from '../config';
import { NotificationType } from '../consts';

export const executeHttpRequest = (
    path,
    httpMethod,
    data,
    isRelativePath = true,
    options = {},
) => {
    const method = httpMethod || 'get';
    const url = isRelativePath ? ROOT_URL_V2 + path : path;
    const headers = method !== 'get' ? { 'X-CSRFToken': getCSRFToken() } : {};

    return axios({
        method,
        url,
        headers,
        data,
        onUploadProgress: options.onUploadProgress,
        signal: options.signal,
    });
};

const fetchTodayShifts = searchParams => {
    const startOfToday = moment().startOf('day').toISOString();
    const endOfToday = moment().endOf('day').toISOString();

    const doesQueryStringIncludesSeriesFilter = searchParams.toString().includes('series');

    if (searchParams.toString()) {
        if (doesQueryStringIncludesSeriesFilter) {
            return executeHttpRequest(
                `jobs/?page=1&getall=1&${searchParams.toString()}`,
            );
        }

        return executeHttpRequest(
            `jobs/?page=1&after=${startOfToday}&before=${endOfToday}&${searchParams.toString()}`,
        );
    }

    return executeHttpRequest(
        `jobs/?page=1&after=${startOfToday}&before=${endOfToday}`,
    );
};

export const fetchShifts = searchParams => {
    if (
        !searchParams.get('before') &&
        !searchParams.get('after') &&
        !searchParams.get('getall')
    ) {
        return fetchTodayShifts(searchParams);
    }
    return executeHttpRequest(`jobs/?${searchParams.toString()}`);
};

export const fetchSubscriptions = searchParams =>
    executeHttpRequest(`jobs/?type=subscription&${searchParams.toString()}`);

export const getTrackingUrl = searchParams => {
    const orderBy = '+start_time';
    if (
        !searchParams.get('before') &&
        !searchParams.get('after') &&
        !searchParams.get('getall')
    ) {
        const startOfToday = moment().startOf('day').toISOString();
        const endOfToday = moment().endOf('day').toISOString();
        return (
            `jobs/?bookings_status=OK&ordering=${orderBy}&after=${startOfToday}&before=${endOfToday}&${searchParams.toString()}`
        );
    }
    return (
        `jobs/?bookings_status=OK&ordering=${orderBy}&${searchParams.toString()}`
    );
};

export const fetchTracking = searchParams => {
    const trackingUrl = getTrackingUrl(searchParams);
    return executeHttpRequest(trackingUrl);
};

export const fetchTrackingDataForMap = () => {
    const startOfToday = moment().startOf('day').toISOString();
    const endOfToday = moment().endOf('day').toISOString();
    return executeHttpRequest(
        `jobs/?bookings_status=OK&after=${startOfToday}&before=${endOfToday}&paginate_by=1000`,
    );
};

export const fetchBookings = searchParams => {
    searchParams.set('has_bookings', 1);
    if (
        !searchParams.get('before') &&
        !searchParams.get('after') &&
        !searchParams.get('getall')
    ) {
        return fetchTodayShifts(searchParams);
    }
    return executeHttpRequest(`jobs/?${searchParams.toString()}`);
};

export const fetchFreelancers = searchParams =>
    executeHttpRequest(`freelancers/?${searchParams.toString()}`);

export const fetchUnallocatedFreelancers = searchParams =>
    executeHttpRequest(`unallocated-freelancers/?${searchParams.toString()}`);

export const fetchClients = searchParams =>
    executeHttpRequest(`clients/?${searchParams.toString()}`);

export const getPageFromTable = state => (state ? state.page + 1 : 1);

export const fetchJob = jobId => executeHttpRequest(`jobs/${jobId}/`);

export const getMinimalistDetailJobs = query =>
    executeHttpRequest(`jobs/minimalist/?${query.toString()}`);

export const fetchJobApplications = jobId =>
    executeHttpRequest(`jobs/${jobId}/invitations/?applied=1`);

export const fetchJobInvites = jobId =>
    executeHttpRequest(`jobs/${jobId}/invitations/`);

export const cancelShift = shift =>
    executeHttpRequest(`jobs/${shift._original.id}/`, 'delete');

export const jobInvitations = jobId =>
    executeHttpRequest(`jobs/${jobId}/invitations/`, 'post', '{}');

export const acceptInvitation = ({ jobId, invitationId }) =>
    executeHttpRequest(
        `jobs/${jobId}/invitations/${invitationId}/accept/`,
        'post',
        '{}',
    );

export const rejectInvitation = ({ jobId, invitationId }) =>
    executeHttpRequest(
        `jobs/${jobId}/invitations/${invitationId}/decline/`,
        'post',
        {},
    );

export const fetchInductionSessions = ({ afterDate, beforeDate }) =>
    executeHttpRequest(
        `${ROOT_URL}induction-sessions/?after=${afterDate.toString()}&before=${beforeDate.toString()}`,
        'get',
        {},
        false
    );

export const updateJobStatus = ({ jobId, status, data = {} }) =>
    executeHttpRequest(`jobs/${jobId}/${status}/`, 'post', data);

export const uploadFreelancerCSV = ({ service, skipInvites, csvFile }) => {
    const url = `${ROOT_URL_V2}freelancers/import/`;
    const formData = new FormData();
    formData.append('service', service);
    formData.append('skip_invites', !!skipInvites);
    formData.append('freelancer_csv', csvFile);
    const config = {
        headers: {
            'X-CSRFToken': getCSRFToken(),
            'content-type': 'multipart/form-data',
        },
    };
    return post(url, formData, config);
};

export const createFreelancers = ({ skipInvites, freelancers }) =>
    executeHttpRequest('freelancers/import/', 'post', {
        skip_invites: !!skipInvites,
        freelancer_json: freelancers,
    });

export const sendSMS = ({
    recipients,
    exclusive,
    filter,
    message,
    isUnallocatedFreelancersRoute,
    successCallback,
    errorCallback,
}) => {
    const payload = { message };
    let url = isUnallocatedFreelancersRoute
        ? 'unallocated-freelancers/text/'
        : 'freelancers/text/';
    if (recipients.length > 1 || exclusive) {
        if (exclusive) {
            payload.excluded_freelancers = recipients;
            url += filter;
        } else {
            payload.freelancers = recipients;
        }
    } else {
        url = isUnallocatedFreelancersRoute
            ? `unallocated-freelancers/${recipients[0]}/text/`
            : `freelancers/${recipients[0]}/text/`;
    }
    return executeHttpRequest(url, 'post', payload)
        .then(response => successCallback(response))
        .catch(error => {
            errorCallback(error);
        });
};

export const sendNotification = ({
    recipients,
    title,
    message,
    notificationType,
    attachments,
    successCallback,
    errorCallback,
    isUnallocatedFreelancersRoute,
}) => {
    const payload = {
        title: title || 'Notification',
        message,
        notification_type: notificationType || NotificationType.PUSH_ONLY
    };
    const url = isUnallocatedFreelancersRoute
        ? 'unallocated-freelancers/notification/'
        : 'freelancers/notification/';
    if (recipients.length > 0) {
        payload.freelancers = recipients;
    }
    
    // Handle attachments if any
    if (attachments && attachments.length > 0) {
        const formData = new FormData();
        
        // Add the main payload fields
        Object.keys(payload).forEach(key => {
            if (key === 'freelancers' && Array.isArray(payload[key])) {
                // Handle freelancers array specially - append each ID individually
                payload[key].forEach(freelancerId => {
                    formData.append('freelancers', freelancerId);
                });
            } else if (Array.isArray(payload[key])) {
                payload[key].forEach(item => formData.append(`${key}[]`, item));
            } else {
                formData.append(key, payload[key]);
            }
        });
        
        // Add attachments
        attachments.forEach(file => {
            formData.append('attachments', file);
        });
        
        return executeHttpRequest(url, 'post', formData)
            .then(response => successCallback(response))
            .catch(error => {
                errorCallback(error);
            });
    }
    
    return executeHttpRequest(url, 'post', payload)
        .then(response => successCallback(response))
        .catch(error => {
            errorCallback(error);
        });
};

export const fetchNotifications = () =>
    executeHttpRequest('notifications/?is_read=0');
export const fetchNotificationsCounters = query => {
    const queryString = query ? `?${query}` : '';
    return executeHttpRequest(`notifications/counters/${queryString}`);
};

export const getBookingCounters = query =>
    executeHttpRequest(`jobs/counters/?${query.toString()}`);

export const fetchSettings = () =>
    executeHttpRequest('settings/');
